// ./src/pages/private/EventTicketsPage/PriceTiersDrawer.tsx
//
// "Price by tickets sold" for one ticket type (ROADMAP #162, owner 2026-10-15: "for the first 40 sales price 10, for the
// next 40 - price 15, then regular price"). Rows of "the next N tickets at $X", in order; after the last row the type's
// own price applies. Saved in ONE call (`ticketPriceTiersSet`), which rewrites the ladder's rules; an empty list removes
// the ladder.
//
// ⓘ What the buyer pays is decided per ticket on the server: an order crossing a tier pays each tier's price for its own
//   tickets. Presence-only checks here (each row has a count and a price); the server checks the rest and names the row.
// ⓘ Stacked over the rules drawer (local state, like PriceRuleDrawer): it edits part of that drawer's list.

import React, { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Box, Button, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { OpResult } from '@sdflc/api-helpers';
import { formatCurrency } from '@em/utils';

import { TicketPriceRuleService } from '@/utils/api';
import { useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from '@/hooks';
import { getIndexedFormErrors, formatErrors } from '@/utils';
import { AppDrawer, AppIcon, NumberField, currencyAffixes } from '@/components/generic';

import { PRICE_TIERS_MAX, TierRow, tierRanges } from './priceTiers';

export interface PriceTiersDrawerProps {
  opened: boolean;
  onClose: () => void;
  ticketTypeId: string;
  /** The type's own price — what applies after the last tier. */
  regularPrice: number;
  currencyCode: string;
  /** The ladder as it stands (from the type's rules). */
  initial: Array<{ quantity: number; price: number }>;
  onSaved: () => void;
}

interface TiersForm {
  rows: TierRow[];
}

export const PriceTiersDrawer: React.FC<PriceTiersDrawerProps> = ({ opened, onClose, ticketTypeId, regularPrice, currencyCode, initial, onSaved }) => {
  const { t } = useTranslation(['events', 'common']);
  const [form, setForm] = useState<TiersForm>({ rows: [] });
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  // A row's refusal (`tiers[i].quantity` / `tiers[i].price`) lands AT that row's input; anything else above Save.
  const { byIndex: rowErrors, genericErrors } = useMemo(() => getIndexedFormErrors(submitError, 'tiers'), [submitError]);
  const hasRowErrors = Object.values(rowErrors).some((row) => Object.values(row).some(Boolean));

  const { isDirty, reseed, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      clear();
      setSubmitError(null);

      return;
    }

    // A new ladder starts with one empty row to fill in.
    const seeded: TiersForm = { rows: initial.length > 0 ? initial.map((r) => ({ quantity: r.quantity, price: r.price })) : [{ quantity: null, price: null }] };
    setForm(reseed(seeded, ticketTypeId));
    // ⓘ Seeded when the drawer opens (for this type) — a re-read of the rules behind it does not wipe what is typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, ticketTypeId]);

  const ranges = useMemo(() => tierRanges(form.rows), [form.rows]);
  const filledRows = form.rows.filter((r) => r.quantity != null || r.price != null);
  // Presence only: every started row has both a count and a price.
  const complete = filledRows.every((r) => r.quantity != null && r.quantity >= 1 && r.price != null);
  const canSave = !saving && complete;
  const lastTo = [...ranges].reverse().find((r) => r != null)?.to ?? 0;

  const setRow = (i: number, patch: Partial<TierRow>) =>
    setForm((prev) => ({ rows: prev.rows.map((row, j) => (j === i ? { ...row, ...patch } : row)) }));
  const removeRow = (i: number) => setForm((prev) => ({ rows: prev.rows.filter((_row, j) => j !== i) }));
  const addRow = () => setForm((prev) => ({ rows: [...prev.rows, { quantity: null, price: null }] }));

  const save = async () => {
    if (!canSave) {
      return;
    }

    setSaving(true);
    setSubmitError(null);
    const tiers = filledRows.map((r) => ({ quantity: Number(r.quantity), price: Number(r.price) }));
    const result = await TicketPriceRuleService.setTiers(ticketTypeId, tiers);
    setSaving(false);

    if (!result.didSucceed()) {
      // Field refusals at their row, the rest above Save.
      setSubmitError(result);

      return;
    }

    // No toast — the tiers listed in the rules drawer are the confirmation.
    onSaved();
    onClose();
  };

  useModalShortcuts({ onConfirm: save, canConfirm: canSave, enabled: opened });

  const money = (n: number): string => formatCurrency(n, currencyCode);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t('events:tiers.title', { defaultValue: 'Price by tickets sold' })}
      size="md"
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={hasRowErrors}
      actionType="save-cancel"
      onConfirm={save}
      confirmLabel={t('common:buttons.save', { defaultValue: 'Save' })}
      cancelLabel={t('common:buttons.cancel', { defaultValue: 'Cancel' })}
      confirmDisabled={!canSave}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <Text size="md" c="dimmed">
          {t('events:tiers.hint', {
            defaultValue:
              'The first tickets sell at the first price, the next ones at the next — then the regular price. A buyer whose order crosses a tier pays each tier’s price for its own tickets.',
          })}
        </Text>

        {form.rows.map((row, i) => {
          const range = ranges[i];

          return (
            <Box key={i}>
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <NumberField
                  name={`tiers[${i}].quantity`}
                  label={t('events:tiers.quantity', { defaultValue: 'Tickets' })}
                  value={row.quantity}
                  onChange={(_n, v) => setRow(i, { quantity: v })}
                  min={1}
                  allowDecimal={false}
                  allowNegative={false}
                  error={rowErrors[i]?.quantity}
                  size="md"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <NumberField
                  name={`tiers[${i}].price`}
                  label={t('events:tiers.price', { defaultValue: 'Price each' })}
                  value={row.price}
                  onChange={(_n, v) => setRow(i, { price: v })}
                  min={0}
                  decimalScale={2}
                  allowNegative={false}
                  error={rowErrors[i]?.price}
                  size="md"
                  style={{ flex: 1, minWidth: 0 }}
                  {...currencyAffixes(currencyCode)}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  mt={30}
                  aria-label={t('events:tiers.remove', { defaultValue: 'Remove this tier' })}
                  onClick={() => removeRow(i)}
                  disabled={saving}
                >
                  <AppIcon iconName="X" size={18} />
                </ActionIcon>
              </Group>
              {range && (
                <Text size="md" c="dimmed" mt={4}>
                  {range.from === range.to
                    ? t('events:tiers.rangeOne', { defaultValue: 'Ticket {{from}}', from: range.from })
                    : t('events:tiers.range', { defaultValue: 'Tickets {{from}}–{{to}}', from: range.from, to: range.to })}
                </Text>
              )}
            </Box>
          );
        })}

        <Group>
          <Button variant="light" size="md" leftSection={<AppIcon iconName="Plus" size={16} />} onClick={addRow} disabled={saving || form.rows.length >= PRICE_TIERS_MAX}>
            {t('events:tiers.add', { defaultValue: 'Add a tier' })}
          </Button>
        </Group>

        <Text size="md">
          {filledRows.length === 0
            ? t('events:tiers.none', { defaultValue: 'No tiers — every ticket sells at the regular price ({{price}}). Save to remove the tiers.', price: money(regularPrice) })
            : t('events:tiers.after', { defaultValue: 'From ticket {{n}} on: the regular price ({{price}}).', n: lastTo + 1, price: money(regularPrice) })}
        </Text>
      </Stack>
    </AppDrawer>
  );
};
